import React from 'react';
import { DefaultShipping } from '../partials/shipping/DefaultShipping';
import { FULFILLMENT_METHODS } from '../../components/constants';
import {
  getLiveGoodsStock,
  isApplianceDeliveryError,
  isApplianceDeliveryOutOfStock,
  isApplianceDeliveryUnavailable,
  shouldShowDeliveryFallback
} from '../../components/helper/utils';
import {
  getApplianceEarliestDeliveryDate,
  getApplianceEarliestDeliveryDateFromSearch,
  getApplianceInventory,
  getBODFSFulfillment,
  getDeliveryService,
  getDisplayableInventory,
  getExcludedStateSth,
  getSTHFulfillment,
  getShippingThreshold,
  getStateCode,
  hasFreeShipping,
  isAppliance,
  isApplianceConfigurableBundle,
  isBODFS,
  isBackordered,
  isBackorderedWithDate,
  isBodfsWithQuantityAvailable,
  isBodfsWithQuantityUnknown,
  isDiscontinued,
  isItemGCC,
  isSTH,
  isStateExcluded,
  isSthOutOfStock,
  meetsApplianceDeliveryThreshold
} from './pod-fulfillment-utils';
import { dataModel } from '../../components/dataModel';
import { EmptyShippingMessage } from '../partials/shipping/EmptyShippingMessage';
import {
  BundleFulfillmentMessageShipping
} from '../partials/shipping/BundleFulfillmentMessageShipping';

/**
 * Tags a template render function with the FULFILLMENT_METHODS value its outcome implies,
 * so consumers (e.g. a selectable pod) can read `template.fulfillmentMethod`.
 * Disabled/empty outcomes are left untagged and read back as null.
 */
const withMethod = (renderFn, fulfillmentMethod = null) => {
  // eslint-disable-next-line no-param-reassign
  renderFn.fulfillmentMethod = fulfillmentMethod;
  return renderFn;
};

const getApplianceAvailabilityComponent = ({
  availabilityData,
  applianceDeliveryDate,
  bundleFlag,
  enableApplianceDeliveryCharge,
  meetsThreshold,
  shippingMessageStyle,
  shippingThreshold,
  product,
  isNarrowPod
}) => {
  const showDeliveryFallback = shouldShowDeliveryFallback(availabilityData, !!bundleFlag);
  if (!applianceDeliveryDate && showDeliveryFallback) {
    return withMethod(() => (
      <DefaultShipping
        iconType="success"
        shippingMessage={bundleFlag ? 'View package for details' : 'Available'}
        shippingMessageStyle={shippingMessageStyle}
        shippingThreshold={shippingThreshold}
        product={product}
        isNarrowPod={isNarrowPod}
        showDeliveryFallback
      />
    ), FULFILLMENT_METHODS.APPLIANCE);
  }

  const deliveryAvailability = availabilityData?.DeliveryAvailabilityResponse?.deliveryAvailability;
  const showUnavailable = [
    isApplianceDeliveryOutOfStock,
    isApplianceDeliveryUnavailable,
    isApplianceDeliveryError
  ].some((helper) => helper(deliveryAvailability));

  if (showUnavailable) {
    return () => (
      <DefaultShipping
        iconType="close"
        shippingMessage="Unavailable"
        product={product}
        shippingThreshold={shippingThreshold}
        shippingMessageStyle="sui-text-subtle"
        isNarrowPod={isNarrowPod}
      />
    );
  }

  const shippingMessage = meetsThreshold ? 'Free' : '';

  return withMethod(() => (
    <DefaultShipping
      iconType="success"
      applianceDeliveryDate={applianceDeliveryDate}
      shippingMessage={enableApplianceDeliveryCharge ? 'Available' : shippingMessage}
      shippingMessageStyle={shippingMessageStyle}
      shippingThreshold={shippingThreshold}
      product={product}
      isAppliance
      isNarrowPod={isNarrowPod}
    />
  ), FULFILLMENT_METHODS.APPLIANCE);
};

export const getApplianceScenarios = ({
  availabilityData,
  checkAvailabilityButton,
  bundleFlag,
  customRender,
  enableApplianceDeliveryCharge,
  enableFreeDeliveryForExchange,
  applianceDeliveryThreshold,
  isExchangeCustomer,
  isPuertoRicoZip,
  product,
  isNarrowPod
}) => {
  const earliestDeliveryDate = !isPuertoRicoZip
    ? getApplianceEarliestDeliveryDateFromSearch(product)
    : null;

  let meetsThreshold = false;
  const shippingMessage = checkAvailabilityButton;
  let shippingMessageStyle = 'sui-text-success';
  let shippingThreshold = null;
  if (isExchangeCustomer) {
    meetsThreshold = !!enableFreeDeliveryForExchange;
  } else {
    meetsThreshold = meetsApplianceDeliveryThreshold(product?.pricing, applianceDeliveryThreshold);
    // FEATURE SWITCH OVERRIDE
    if (enableApplianceDeliveryCharge) {
      shippingMessageStyle = 'sui-text-primary';
    }
  }

  if (!meetsThreshold) {
    shippingThreshold = applianceDeliveryThreshold;
  }

  if (availabilityData || earliestDeliveryDate) {
    const applianceDeliveryDate = getApplianceEarliestDeliveryDate(availabilityData) || earliestDeliveryDate;

    return getApplianceAvailabilityComponent({
      availabilityData,
      applianceDeliveryDate,
      bundleFlag,
      enableApplianceDeliveryCharge,
      meetsThreshold,
      shippingMessageStyle,
      shippingThreshold,
      product,
      isNarrowPod
    });
  }

  return withMethod(() => (
    <DefaultShipping
      iconType="success"
      shippingMessageStyle={shippingMessageStyle}
      shippingMessage={shippingMessage}
      customRender={customRender}
      applianceQuantity={getApplianceInventory(product)}
      product={product}
      shippingThreshold={shippingThreshold}
      isAppliance={typeof shippingMessage !== 'object'}
      isNarrowPod={isNarrowPod}
    />
  ), FULFILLMENT_METHODS.APPLIANCE);
};

const getSthOnlyScenarios = ({
  customRender,
  excludedState, hasSameDayFilter, product, isNewFulfillmentPodMessagingEnabled, isNarrowPod
}) => {
  if (hasSameDayFilter) {
    return null;
  }
  if (excludedState) {
    return () => (
      <DefaultShipping
        excludedState={excludedState}
        customRender={customRender}
        product={product}
        isNarrowPod={isNarrowPod}
      />
    );
  }
  if (isBackorderedWithDate(product) || isBackordered(product)) {
    return withMethod(() => (
      <DefaultShipping
        iconType="success"
        shippingMessage="Standard"
        customRender={customRender}
        product={product}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        isNarrowPod={isNarrowPod}
      />
    ), FULFILLMENT_METHODS.STH);
  }
  if (isSthOutOfStock(product)) {
    return () => (
      <DefaultShipping
        shippingMessage="Unavailable"
        customRender={customRender}
        product={product}
        shippingMessageStyle="sui-text-subtle"
        isNarrowPod={isNarrowPod}
      />
    );
  }
  return null;
};

const getScheduledDeliveryComponent = ({
  bodfsFulfillment,
  customRender,
  product,
  maxQtyToDisplay,
  bodfsDisplayInventory,
  hasSameDayFilter,
  isNewFulfillmentPodMessagingEnabled,
  isNarrowPod
}) => {
  return withMethod(() => (
    <DefaultShipping
      iconType="success"
      shippingMessage="Scheduled"
      displayQuantity={isNewFulfillmentPodMessagingEnabled ? maxQtyToDisplay : bodfsDisplayInventory}
      customRender={customRender}
      product={product}
      deliveryMessage={bodfsFulfillment?.deliveryMessage}
      hasSameDayFilter={!!hasSameDayFilter}
      isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      isNarrowPod={isNarrowPod}
    />
  ), FULFILLMENT_METHODS.BODFS);
};

const getShippingComponent = ({
  shippingThreshold,
  sthFulfillment,
  customRender,
  product,
  displayQuantityValue,
  hasSameDayFilter,
  isNewFulfillmentPodMessagingEnabled,
  isNarrowPod
}) => {
  if (hasFreeShipping(product)) {
    return withMethod(() => (
      <DefaultShipping
        iconType="success"
        shippingMessageStyle="sui-text-success"
        shippingMessage="Free"
        displayQuantity={displayQuantityValue}
        customRender={customRender}
        product={product}
        deliveryMessage={sthFulfillment?.deliveryMessage}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        isNarrowPod={isNarrowPod}
      />
    ), FULFILLMENT_METHODS.STH);
  }

  if (typeof shippingThreshold === 'number') {
    const shippingMessage = shippingThreshold > 0 ? `Free with $${shippingThreshold} order` : 'Free';
    return withMethod(() => (
      <DefaultShipping
        iconType="success"
        shippingMessageStyle="sui-text-success"
        shippingMessage={shippingMessage}
        shippingThreshold={shippingThreshold}
        displayQuantity={displayQuantityValue}
        customRender={customRender}
        product={product}
        deliveryMessage={sthFulfillment?.deliveryMessage}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        isNarrowPod={isNarrowPod}
      />
    ), FULFILLMENT_METHODS.STH);
  }

  return withMethod(() => (
    <DefaultShipping
      iconType="success"
      shippingMessage="Standard"
      displayQuantity={displayQuantityValue}
      customRender={customRender}
      product={product}
      deliveryMessage={sthFulfillment?.deliveryMessage}
      hasSameDayFilter={hasSameDayFilter}
      isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      isNarrowPod={isNarrowPod}
    />
  ), FULFILLMENT_METHODS.STH);
};

const getMixedScenarios = ({
  bodfsFulfillment,
  customRender,
  customer,
  hasSameDayFilter,
  product,
  sthFulfillment,
  isNewFulfillmentPodMessagingEnabled,
  isNarrowPod
}) => {
  if (!isSTH(product)) {
    return null;
  }

  const bodfsDisplayInventory = getDisplayableInventory(product, customer, FULFILLMENT_METHODS.BODFS);
  const sthDisplayInventory = getDisplayableInventory(product, customer, FULFILLMENT_METHODS.STH);
  const shippingThreshold = getShippingThreshold(product);
  const excludedState = getExcludedStateSth(product);
  const maxQtyToDisplay = Math.max(bodfsDisplayInventory, sthDisplayInventory);

  // Handle BODFS with excluded state or same day filter
  if (isBODFS(product) && (excludedState || hasSameDayFilter)) {
    return getScheduledDeliveryComponent({
      bodfsFulfillment,
      customRender,
      product,
      maxQtyToDisplay,
      bodfsDisplayInventory,
      hasSameDayFilter,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod
    });
  }

  // Check STH-only scenarios first
  const sthScenario = getSthOnlyScenarios({
    customRender,
    excludedState,
    hasSameDayFilter,
    product,
    isNewFulfillmentPodMessagingEnabled,
    isNarrowPod
  });

  if (sthScenario) {
    return sthScenario;
  }

  // Handle remaining BODFS or non-same-day scenarios
  if (isBODFS(product) || !hasSameDayFilter) {
    const displayQuantityValue = isNewFulfillmentPodMessagingEnabled ? maxQtyToDisplay : sthDisplayInventory;

    return getShippingComponent({
      shippingThreshold,
      sthFulfillment,
      customRender,
      product,
      displayQuantityValue,
      hasSameDayFilter,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod
    });
  }

  return null;
};

const getBodfsScenarios = ({
  bodfsFulfillment,
  customRender, customer, product, isNewFulfillmentPodMessagingEnabled, isNarrowPod
}) => {

  const bodfsDisplayInventory = getDisplayableInventory(product, customer, FULFILLMENT_METHODS.BODFS);
  const sthDisplayInventory = getDisplayableInventory(product, customer, FULFILLMENT_METHODS.STH);
  const maxQtyToDisplayBodfs = Math.max(bodfsDisplayInventory, sthDisplayInventory);

  if (!isBODFS(product)) {
    return null;
  }
  if (isBodfsWithQuantityUnknown(product) || !isBodfsWithQuantityAvailable(product)) {
    return () => (
      <DefaultShipping
        shippingMessage="Unavailable"
        customRender={customRender}
        product={product}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        isNarrowPod={isNarrowPod}
      />
    );
  }
  return withMethod(() => (
    <DefaultShipping
      iconType="success"
      shippingMessage="Scheduled"
      displayQuantity={isNewFulfillmentPodMessagingEnabled ? maxQtyToDisplayBodfs : bodfsDisplayInventory}
      customRender={customRender}
      product={product}
      deliveryMessage={bodfsFulfillment?.deliveryMessage}
      isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      isNarrowPod={isNarrowPod}
    />
  ), FULFILLMENT_METHODS.BODFS);
};

const getUnavailableScenarios = ({ customRender, product, isNewFulfillmentPodMessagingEnabled, isNarrowPod }) => {
  /* -----not STH and not BODFS means no delivery to customer directly----- */
  if (isDiscontinued(product)) {
    return () => <EmptyShippingMessage customRender={customRender} product={product} />;
  }
  return () => (
    <DefaultShipping
      shippingMessage="Unavailable"
      customRender={customRender}
      product={product}
      shippingMessageStyle="sui-text-subtle"
      isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      isNarrowPod={isNarrowPod}
    />
  );
};

export const getShippingTemplate = ({
  availabilityData,
  product,
  customer,
  customRender,
  checkAvailabilityButton,
  isPuertoRicoZip,
  enableApplianceDeliveryCharge,
  enableFreeDeliveryForExchange,
  applianceDeliveryThreshold,
  isExchangeCustomer,
  hasSameDayFilter = false,
  bundleFlag,
  showBundleFulfillmentMessage,
  isNewFulfillmentPodMessagingEnabled,
  isNarrowPod,
  isAppliancePod
}) => {
  const isGCC = isItemGCC(product);

  if (!product || isGCC) {
    return () => (
      <EmptyShippingMessage isGCC={isGCC} customRender={customRender} product={product} />
    );
  }
  /* -----MA Bundle----- */
  // Route appliance bundles from product data so the express delivery date shows even when
  // bundleFlag/isAppliancePod aren't set (otherwise they fall through to the dateless STH path).
  const isApplianceBundle = isApplianceConfigurableBundle(product);
  if (bundleFlag || isApplianceBundle) {
    if (isAppliancePod || isApplianceBundle) {
      return getApplianceScenarios({
        availabilityData,
        checkAvailabilityButton,
        bundleFlag,
        customRender,
        enableApplianceDeliveryCharge,
        enableFreeDeliveryForExchange,
        applianceDeliveryThreshold,
        isExchangeCustomer,
        isPuertoRicoZip,
        product,
        isNarrowPod,
      });
    }
    if (showBundleFulfillmentMessage) {
      return () => <BundleFulfillmentMessageShipping isNarrowPod={isNarrowPod} />;
    }
    return null;
  }

  /* -----LiveGoods----- */
  if (getLiveGoodsStock(product?.fulfillment) === 0) {
    return () => <EmptyShippingMessage customRender={customRender} product={product} />;
  }

  if (isDiscontinued(product)) {
    return () => (
      <DefaultShipping
        shippingMessage="No longer available"
        customRender={customRender}
        product={product}
        shippingMessageStyle="sui-text-subtle"
        isNarrowPod={isNarrowPod}
      />
    );
  }

  /* -----Appliance----- */
  if (isAppliance(product)) {
    return getApplianceScenarios({
      availabilityData,
      checkAvailabilityButton,
      bundleFlag,
      customRender,
      enableApplianceDeliveryCharge,
      enableFreeDeliveryForExchange,
      applianceDeliveryThreshold,
      isExchangeCustomer,
      isPuertoRicoZip,
      product,
      isNarrowPod
    });
  }

  if (!getDeliveryService(product)) {
    // TODO: move sthExcludedShipState to isStateExcluded and remove getStateCode after plp speed
    const sthExcludedShipState = product?.fulfillment?.sthExcludedShipState;
    const zipcodeStateCode = sthExcludedShipState || getStateCode(null, 'sth');
    const excludedStateName = isStateExcluded(zipcodeStateCode, 'sth', product?.fulfillment);
    if (excludedStateName) {
      return () => (
        <DefaultShipping
          excludedState={excludedStateName}
          customRender={customRender}
          product={product}
          isNarrowPod={isNarrowPod}
        />
      );
    }
  }

  const bodfsFulfillment = getBODFSFulfillment(product);
  const sthFulfillment = getSTHFulfillment(product);

  return (
    getMixedScenarios({
      bodfsFulfillment,
      customRender,
      customer,
      hasSameDayFilter,
      product,
      sthFulfillment,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod
    })
    || getBodfsScenarios({
      bodfsFulfillment,
      customRender,
      customer,
      product,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod
    })
    || getUnavailableScenarios({ customRender, product, isNarrowPod })
  );
};

getShippingTemplate.dataModel = dataModel;

/**
 * The delivery fulfillment method the SHIPPING POD would show for this product
 * (ShipToHome / DeliverFromStore / DirectDelivery — DirectDelivery for appliances), or null when
 * delivery isn't a selectable option. A pure delegate to getShippingTemplate: it only reads the
 * `.fulfillmentMethod` tag off the returned template (the render functions are never invoked), so
 * it can never drift from the shipping pod's radio tagging — including edge cases like a
 * discontinued appliance, which the template resolves to null before its appliance branch.
 */
export const getDeliveryFulfillmentMethod = (product, { hasSameDayFilter = false } = {}) => {
  return getShippingTemplate({ product, hasSameDayFilter })?.fulfillmentMethod ?? null;
};
